/**
 * Support (CLAUDE.md 12.3): the reporter's list of requests.
 *
 * A request is a page, not a modal, because a support conversation outlives the
 * moment it was opened: the engineer comes back to read the reply, to add the
 * screenshot they were asked for, and to say whether it worked. Each row leads to
 * that thread; "New request" leads to a page of its own.
 */
import { Link, useSearchParams } from 'react-router-dom'
import { LifeBuoy, MessageSquare, Paperclip, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useTickets, type TicketListFilter } from '@/api/support'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { TicketBallPill, TicketStatusPill, TicketTypeIcon } from '@/components/support/TicketBadges'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

const FILTERS: TicketListFilter[] = ['active', 'resolved', 'closed', 'all']

export function SupportPage() {
  const { t, i18n } = useTranslation()
  const [params, setParams] = useSearchParams()
  const raw = params.get('view')
  const filter: TicketListFilter = FILTERS.includes(raw as TicketListFilter)
    ? (raw as TicketListFilter)
    : 'active'

  const tickets = useTickets(filter)

  return (
    <WorkspaceChrome title={t('support.title')}>
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('support.title')}</h2>
          <Button asChild variant="primary">
            <Link to="/support/new">
              <Plus size={16} aria-hidden />
              {t('support.new')}
            </Link>
          </Button>
        </div>

        <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label={t('common.status')}>
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={cn(
                'h-9 whitespace-nowrap rounded-md px-3 text-sm',
                filter === value
                  ? 'bg-surface-raised font-medium text-text'
                  : 'text-text-muted hover:text-text',
              )}
              onClick={() => setParams(value === 'active' ? {} : { view: value })}
            >
              {t(`support.filter.${value}`)}
            </button>
          ))}
        </div>

        {tickets.isLoading && <Skeleton className="h-40" />}

        {tickets.data && tickets.data.length === 0 && (
          <Card>
            <CardBody className="py-10 text-center">
              <LifeBuoy size={28} className="mx-auto mb-2 text-text-muted" aria-hidden />
              <p className="font-medium">
                {filter === 'active' ? t('support.empty') : t('support.empty_filtered')}
              </p>
              <p className="mt-1 text-sm text-text-muted">{t('support.empty_hint')}</p>
            </CardBody>
          </Card>
        )}

        <ul className="space-y-2">
          {tickets.data?.map((ticket) => (
            <li key={ticket.id}>
              <Link
                to={`/support/${ticket.id}`}
                className="block rounded-lg border border-border bg-surface p-3 hover:border-[var(--border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              >
                <div className="flex items-start gap-3">
                  <TicketTypeIcon type={ticket.type} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="min-w-0 truncate font-medium">{ticket.title}</span>
                      {ticket.module_id && (
                        <span className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-[11px] text-text-muted">
                          {ticket.module_id}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                      <TicketStatusPill status={ticket.status} />
                      <TicketBallPill
                        awaitingStaff={ticket.awaiting_staff}
                        messageCount={ticket.message_count}
                      />
                      {ticket.message_count > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare size={12} aria-hidden />
                          {ticket.message_count}
                        </span>
                      )}
                      {ticket.attachment_count > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Paperclip size={12} aria-hidden />
                          {ticket.attachment_count}
                        </span>
                      )}
                      <span className="ms-auto">
                        {formatRelative(ticket.last_activity_at, i18n.language)}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </WorkspaceChrome>
  )
}
